import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { DocumentService } from '../../document.service';
import { DocumentModel } from '../../document.model';
import { FiEditNamesMatComponent } from '@fi-app/components/fi-edit-names-mat/fi-edit-names-mat.component';
import { ModuleType } from '@fi-shared/types/module-type';
import { TemplateLayoutLanguageService } from '@fi-app/template/template-layout-language.service';
import { ILightWeightTemplate } from '@fi-app/document-template/document-template';
import { ILightWeightObject } from '@fi-shared/models';
import { fiHasAccessToApplicationType } from '@fi-shared/utils';
import { AccessRightType, ApplicationSettingsType } from '@fi-shared/types';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiTagsService } from '@fi-shared/fi-shared-services';
import { fiDoesLightWeightObjectHaveChildren } from '@fi-shared/utils/fi-does-light-weight-object-have-children';
import { IDocumentDefaultTemplateSettings } from '@fi-app/document/document';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { TemplateLayoutModel } from '@fi-app/template/template-layout.model';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { BaseItemModelService } from '@fi-app/template/base-item.model';
import { NgModel } from '@angular/forms';
import { ILightWeightTag } from '@fi-shared/fi-tag2/fi-tag2';

@Component({
    selector: 'fi-document-edit-general',
    templateUrl: './general.component.html',
    styleUrls: ['./general.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [{ provide: FiComponentsContainer, useExisting: DocumentEditGeneralComponent }],
    standalone: false,
})
export class DocumentEditGeneralComponent extends FiComponentsContainer implements OnInit, OnChanges {
    @Input('document') Document: DocumentModel;

    @Input() selectedLanguageId: number;
    @Input() documentTemplateLayout: TemplateLayoutModel;
    @Input() defaultTemplateSettings: IDocumentDefaultTemplateSettings;
    @Input() initialTemplate;
    @Input() documentTemplates: ILightWeightTemplate[];
    //@ViewChildren('nameError') nameErrorList: QueryList<MessagesDirective>;

    DefaultContentLanguageId: number;
    isNameRequiredFn: (contentName: IContentLanguageLabelModel) => boolean;
    ModuleType = ModuleType;
    selectedContentName;
    availableTags: ILightWeightTag[] = [];
    //optionDisabledFn = fiDoesLightWeightObjectHaveChildren;

    // https://chainformation.atlassian.net/browse/FI-6016
    // allow tags select on any level
    optionDisabledFn = (item: ILightWeightTag, array: ILightWeightTag[]) => item.IsDisabled; // user has no access to tag

    hasRightsToAddTags: boolean = false;

    constructor(
        private contentLanguageService: ContentLanguagesService,
        private documentService: DocumentService,
        private templateLayoutLanguageService: TemplateLayoutLanguageService,
        private fiUserDataService: FiUserDataService,
        private fiTagsService: FiTagsService,
    ) {
        super();
    }

    ngOnInit() {
        this.DefaultContentLanguageId = this.contentLanguageService.GetDefaultLanguageId();
        this.isNameRequiredFn = this.isNameRequired.bind(this);
        this.getAvailableTags();
        this.hasRightsToAddTags = fiHasAccessToApplicationType(
            ApplicationSettingsType.Tag,
            AccessRightType.Write,
            this.fiUserDataService.getUserData().ModuleAccessRights,
        );
    }

    private getAvailableTags(): void {
        this.documentService.GetDocumentTagsByUserAccessRights().then((response) => {
            this.availableTags = [...response];
        });
    }

    addNewTag(tagName: string): void {
        this.fiTagsService
            .saveTag(this.fiTagsService.getNewTagModel(tagName, ModuleType.Document))
            .then((id: number) => {
                const newTag: ILightWeightTag = {
                    Id: id,
                    Name: tagName,
                    ParentId: null,
                    Level: 0,
                };

                this.availableTags.push(newTag);
                this.Document.TagIds = [...this.Document.TagIds, newTag.Id];
            });
    }

    IsDefaultLanguage(ContentLanguageId) {
        return this.contentLanguageService.GetDefaultLanguageId() == ContentLanguageId;
    }

    isNameRequired() {
        return !this.Document.Names.some((name: IContentLanguageLabelModel) => name.Value);
        //
        //
        // let isEmpty = true;
        // if (this.documentTemplateLayout) {
        //     this.documentTemplateLayout.placeholders.forEach(placeholder => {
        //         if (isEmpty && placeholder.items) {
        //             isEmpty = this.checkIsEmpty(placeholder.items, contentName.ContentLanguageId);
        //         }
        //         if (isEmpty && placeholder.children) {
        //             placeholder.children.forEach(child => {
        //                 if (isEmpty) {
        //                     isEmpty = this.checkIsEmpty(child.items, contentName.ContentLanguageId);
        //                 }
        //             });
        //         }
        //     });
        // }
        //
        // if (!isEmpty) {
        //     return true;
        // }
        //
        // // FI-1735 Change request for logic when saving pages
        // // 1. We should not force users to give a page a name in default language if there's no CONTENT added to the default langauge version of the page.
        // // empty default language required only in case other languages are empty
        // if (this.IsDefaultLanguage(contentName.ContentLanguageId) && this.isContentEmpty()) {
        //     return true;
        // }
        //
        // return false;
    }

    isContentEmpty() {
        if (!this.documentTemplateLayout) return true;

        const layoutNotEmptyItems = this.templateLayoutLanguageService.iterateTemplateLayoutItems(
            this.documentTemplateLayout,
            (item) => item.model.Contents.some((content) => !this.isItemEmpty(item, content.ContentLanguageId)),
        );

        return layoutNotEmptyItems.length === 0;
    }

    isItemEmpty(item: BaseItemModelService, contentLanguageId: number) {
        return item.IsEmpty(contentLanguageId) || this.isItemEqualToTemplate(item, contentLanguageId);
    }

    checkIsEmpty(items: Array<any>, ContentLanguageId) {
        return items.every((item) => this.isItemEmpty(item, ContentLanguageId));
    }

    // FI-1735 Change request for logic when saving pages
    // Could we implement a change that checks if language version of a page actually has _added _content
    // or if the content in a certain language is only content from the template.
    // If some language version(s) of a page has content only from it's page template,
    // it should be considered "empty". So when saving a page, I shouldn't have to name the page in
    // a certain language if I haven't added any content manually.
    isItemEqualToTemplate(item: BaseItemModelService, contentLanguageId: number) {
        const initialPlaceholder = this.initialTemplate.PlaceHolderCollection.find(
            (item) => item.Id === item.model?.PlaceHolderId,
        );
        if (!initialPlaceholder) return false;

        const initialPlaceholderControl = initialPlaceholder.ControlCollection.find(
            (item) => item.Id === item.model.Id,
        );
        if (!initialPlaceholderControl) return false;

        const initialContent = initialPlaceholderControl.Contents.find(
            (item) => item.ContentLanguageId === contentLanguageId,
        );
        if (!initialContent) return false;

        const currentContent = item.model.Contents.find((item) => item.ContentLanguageId === contentLanguageId);
        return JSON.stringify(initialContent.Value) === JSON.stringify(currentContent.Value);
    }

    TagsDataSource() {
        return this.documentService.GetDocumentTagsByUserAccessRights();
    }

    // note: not used ?
    formSubmitted() {
        //this.nameErrorList.forEach((item) => item.updateMessagesVisibility());
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.selectedLanguageId) this._updateSelectedContentName();
    }

    _updateSelectedContentName() {
        this.selectedContentName = this.Document.Names.find(
            (name) => name.ContentLanguageId == this.selectedLanguageId,
        );
    }
}
